import { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Mic, Upload, Eye, History } from 'lucide-react';
import { AudioUpload } from '@/components/transcription/AudioUpload';
import { TranscriptionView } from '@/components/transcription/TranscriptionView';
import { TranscriptionHistory } from '@/components/transcription/TranscriptionHistory';
import { useTranscriptions } from '@/hooks/useTranscriptions';
import { Transcription } from '@/types/transcription';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

const CRMTranscripties = () => {
  const [activeTab, setActiveTab] = useState('upload');
  const [selectedTranscription, setSelectedTranscription] = useState<Transcription | null>(null);
  const [allowLargeFiles, setAllowLargeFiles] = useState(false);
  const { transcriptions, isLoading, refetch, deleteTranscription } = useTranscriptions();
  const { userRole } = useAuth();
  const isSuperAdmin = userRole === 'super_admin';

  // Subscribe to realtime updates
  useEffect(() => {
    const channel = supabase
      .channel('transcription-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'transcriptions',
        },
        (payload) => {
          const updated = payload.new as any;
          
          // Update selected transcription if it's the one being updated
          if (selectedTranscription?.id === updated.id) {
            setSelectedTranscription({
              ...updated,
              action_items: updated.action_items || [],
            });
          }
          
          // Show toast for status changes
          if (updated.status === 'completed') {
            toast({
              title: "Transcriptie voltooid!",
              description: `${updated.filename} is succesvol verwerkt.`,
            });
          } else if (updated.status === 'failed') {
            toast({
              title: "Transcriptie mislukt",
              description: updated.error_message || "Er is een fout opgetreden.",
              variant: "destructive",
            });
          }
          
          // Refetch list
          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedTranscription?.id, refetch]);

  const handleUploadComplete = (transcriptionId: string) => {
    // Find the transcription and select it
    const transcription = transcriptions.find(t => t.id === transcriptionId);
    if (transcription) {
      setSelectedTranscription(transcription);
    }
    setActiveTab('bekijken');
    refetch();
  };

  const handleSelectTranscription = (transcription: Transcription) => {
    setSelectedTranscription(transcription);
    setActiveTab('bekijken');
  };

  const handleDeleteTranscription = (id: string) => {
    deleteTranscription.mutate(id);
    if (selectedTranscription?.id === id) {
      setSelectedTranscription(null);
    }
  };

  // Auto-select first transcription when none selected
  useEffect(() => {
    if (!selectedTranscription && transcriptions.length > 0 && !isLoading) {
      setSelectedTranscription(transcriptions[0]);
    }
  }, [transcriptions, selectedTranscription, isLoading]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Mic className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Audio Transcripties</h1>
            <p className="text-muted-foreground">
              Upload audio opnames voor automatische transcriptie en analyse
            </p>
          </div>
        </div>
        
        {isSuperAdmin && (
          <div className="flex items-center gap-2 bg-muted/50 px-3 py-2 rounded-lg">
            <Switch
              id="large-files"
              checked={allowLargeFiles}
              onCheckedChange={setAllowLargeFiles}
            />
            <Label htmlFor="large-files" className="text-sm cursor-pointer">
              Grote bestanden (100MB)
            </Label>
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="upload" className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            <span className="hidden sm:inline">Upload</span>
          </TabsTrigger>
          <TabsTrigger value="bekijken" className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            <span className="hidden sm:inline">Bekijken</span>
          </TabsTrigger>
          <TabsTrigger value="geschiedenis" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            <span className="hidden sm:inline">Geschiedenis</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="mt-6">
          <div className="max-w-xl">
            <AudioUpload onUploadComplete={handleUploadComplete} allowLargeFiles={allowLargeFiles} />
          </div>
        </TabsContent>

        <TabsContent value="bekijken" className="mt-6">
          <TranscriptionView transcription={selectedTranscription} allTranscriptions={transcriptions} />
        </TabsContent>

        <TabsContent value="geschiedenis" className="mt-6">
          <TranscriptionHistory
            transcriptions={transcriptions}
            selectedId={selectedTranscription?.id || null}
            onSelect={handleSelectTranscription}
            onDelete={handleDeleteTranscription}
            isLoading={isLoading}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CRMTranscripties;
